import type { DockviewApi, SerializedDockview } from 'dockview-core';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { PANEL_IDS, type PanelId } from '@trajectory/ui';
import { addPanelToDefaultPosition, buildDefaultLayout } from '@/app/defaultLayout';

interface LayoutPersistState {
  /** The last layout the user arranged, restored on the next load. */
  savedLayout: SerializedDockview | null;
  setSavedLayout: (layout: SerializedDockview | null) => void;
}

export const useLayoutPersistStore = create<LayoutPersistState>()(
  persist(
    (set) => ({
      savedLayout: null,
      setSavedLayout: (savedLayout) => set({ savedLayout }),
    }),
    { name: 'trajectory.layout' },
  ),
);

interface DockviewApiState {
  api: DockviewApi | null;
  /** Which panels currently exist, kept in sync from dockview events so components can react to it. */
  openPanelIds: PanelId[];
  setApi: (api: DockviewApi | null) => void;
  setOpenPanelIds: (ids: PanelId[]) => void;
}

/**
 * Holds the live `DockviewApi` outside of React so that commands, keyboard
 * shortcuts and the toolbar can reach it without threading a ref through the
 * whole panel tree. Deliberately not persisted — `DockviewApi` is not
 * serializable; `useLayoutPersistStore` above holds the JSON that survives a
 * reload. `openPanelIds` mirrors `api.panels` as plain state purely so React
 * components have something to subscribe to, since dockview's own panel list
 * lives outside React's render cycle.
 */
export const useDockviewApiStore = create<DockviewApiState>((set) => ({
  api: null,
  openPanelIds: [],
  setApi: (api) => set({ api }),
  setOpenPanelIds: (openPanelIds) => set({ openPanelIds }),
}));

export function useIsPanelOpen(id: PanelId): boolean {
  return useDockviewApiStore((state) => state.openPanelIds.includes(id));
}

/** Re-reads which known panels currently exist. Call after any layout mutation. */
export function syncOpenPanelIds(api: DockviewApi): void {
  const openIds = new Set(api.panels.map((panel) => panel.id));
  useDockviewApiStore.getState().setOpenPanelIds(PANEL_IDS.filter((id) => openIds.has(id)));
}

export function togglePanel(id: PanelId): void {
  const { api } = useDockviewApiStore.getState();
  if (!api) return;
  const existing = api.getPanel(id);
  if (existing) {
    api.removePanel(existing);
  } else {
    addPanelToDefaultPosition(api, id);
  }
}

export function resetLayout(): void {
  const { api } = useDockviewApiStore.getState();
  if (!api) return;
  buildDefaultLayout(api);
  useLayoutPersistStore.getState().setSavedLayout(api.toJSON());
}

export function saveCurrentLayoutAsDefault(): void {
  const { api } = useDockviewApiStore.getState();
  if (!api) return;
  useLayoutPersistStore.getState().setSavedLayout(api.toJSON());
}
